/* eslint-disable no-console */
import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';

import { appActions } from 'src/store/actions';
import { Page } from 'src/containers';
import { Col, Div, Icon, Row, Text } from '@pinpoint/design-kit';
import { Button, GridWrapper } from 'src/components';
import CONSTANTS from 'src/store/constants';
import { CellActions, CellDate, CellText } from 'src/components/frameworks/cells';
import { CreateFrameworkDialog } from 'src/components/dialogs';
import useFrameworks from 'src/hooks/swr/useFrameworks';
import { createFramework, editFramework, deleteFramework } from 'src/store/api';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);

function Regulatory({ enqueueNotification }) {
  const [gridState, setGridState] = useState({
    sortBy: [],
  });
  const [controlledGridState] = useState({});
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const frameworksQuery = useMemo(() => {
    const query = {
      filters: [],
      order_by: [
        {
          field: 'id',
          direction: 'asc',
        },
      ],
      fields: '*',
    };
    return query;
  }, []);

  const {
    data: frameworks = [],
    error,
    isLoading,
    mutate: getData,
  } = useFrameworks({ query: frameworksQuery, config: { revalidateOnMount: true } });

  const apiStatus = useMemo(() => {
    if (error) {
      return CONSTANTS.API.FAILED;
    }
    if (isLoading) {
      return CONSTANTS.API.STARTED;
    }
    return CONSTANTS.API.SUCCEEDED;
  }, [error, isLoading]);

  const hasData = useMemo(() => frameworks && frameworks.length > 0, [frameworks]);

  const arraySort = useMemo(
    () => (rowA, rowB, columnId) => {
      const a = rowA.values[columnId].join(',');
      const b = rowB.values[columnId].join(',');
      return a > b ? 1 : -1;
    },
    []
  );

  const columns = useMemo(
    () => [
      {
        id: 'name',
        Header: (
          <Text textSize="small" m={{ l: '2rem' }}>
            Name
          </Text>
        ),
        justify: 'left',
        cellJustify: 'left',
        disableSortBy: false,
        Cell: ({ value, row }) =>
          CellText({
            value,
            'data-tip': value,
            textWeight: '400',
            textSize: 'small',
            maxW: '10rem',
            align: 'center',
            m: { l: '2rem', r: '0.5rem' },
            style: { textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' },
          }),
        accessor: ({ name, displayName }) => displayName ?? name,
      },
      {
        id: 'notes',
        Header: <Text textSize="small">Notes</Text>,
        justify: 'left',
        cellJustify: 'left',
        disableSortBy: true,
        Cell: ({ value, row }) =>
          CellText({
            value,
            'data-tip': value,
            textWeight: '400',
            textSize: 'small',
            maxW: '30rem',
            align: 'center',
            m: { r: '0.5rem' },
            style: { textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' },
          }),
        accessor: ({ notes }) => notes,
      },
      {
        id: 'refreshes',
        Header: <Text textSize="small">Refreshes</Text>,
        justify: 'left',
        cellJustify: 'left',
        Cell: ({ value }) =>
          CellText({
            value,
            'data-tip': value,
            textWeight: '400',
            textSize: 'small',
            maxW: '10rem',
            align: 'center',
            m: { r: '0.5rem' },
            style: { textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' },
          }),
        accessor: ({ refreshes }) => refreshes,
        sortType: arraySort,
      },
      {
        id: 'regulationTags',
        Header: <Text textSize="small">State-LOB Tags</Text>,
        justify: 'left',
        cellJustify: 'left',
        Cell: ({ value }) =>
          CellText({
            value,
            'data-tip': value,
            textWeight: '400',
            textSize: 'small',
            maxW: '10rem',
            align: 'center',
            m: { r: '0.5rem' },
            style: { textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' },
          }),
        accessor: ({ regulationTags }) => regulationTags,
        sortType: arraySort,
      },
      {
        Header: <Text textSize="small">Actions</Text>,
        id: 'actions',
        justify: 'left',
        disableSortBy: true,
        accessor: ({ id }) => id,
        Cell: ({ value, row }) => {
          const framework = row?.original;
          return CellActions({
            framework,
            editFramework,
            deleteFramework,
            getData,
          });
        },
      },
      {
        Header: <Text textSize="small">Created</Text>,
        id: 'created',
        cellJustify: 'center',
        Cell: CellDate,
        accessor: ({ createdAt }) => dayjs.utc(createdAt ?? '1970-01-01').toDate(),
        sortType: 'datetime',
      },
      {
        Header: <Text textSize="small">Updated</Text>,
        id: 'updated',
        cellJustify: 'center',
        Cell: CellDate,
        accessor: ({ updatedAt }) => dayjs.utc(updatedAt ?? '1970-01-01').toDate(),
        sortType: 'datetime',
      },
    ],
    [arraySort, getData]
  );

  const openCreateModal = useCallback(() => setCreateModalOpen(true), [setCreateModalOpen]);
  const closeCreateModal = useCallback(() => setCreateModalOpen(false), [setCreateModalOpen]);

  const onCreateFramework = useCallback(
    async (payload) => {
      try {
        await createFramework(payload);
      } catch (err) {
        console.error(err);
        enqueueNotification(`Failed to create framework: ${err.message}`, { autoClose: true, timeout: 5000 });
      }
    },
    [enqueueNotification]
  );

  return (
    <Div flexDir="column" d="flex" h="100%" minH="100%" w="100%" minW="100%" data-debug="content" p={{ x: '-1.15rem' }}>
      <Row data-debug="nav-row" m={{ x: '1.5rem', t: '1.5rem', b: '.9rem' }}>
        {/* <Col justify="left" align="center" gutterWidth="0" d="flex" size="6">

        </Col> */}
        <Col flexGrow="1" d="flex" justify="right" align="center" gutterWidth="0">
          <Button buttonStyle="primary" data-tip="Create a new framework" m={{ r: '0rem' }} onClick={openCreateModal}>
            <Icon name="AddBoxed" color="white" m={{ r: '0.5rem' }} size="1rem" viewBox="0 0 14 14" />
            ADD FRAMEWORK
          </Button>
        </Col>
      </Row>

      <Row flexGrow="1" h="100%" data-debug="activation-row" m={{ x: '1.5rem', b: '.5rem' }}>
        <Col size="12" rounded="md" bg="white" gutterWidth="0">
          <Row data-debug="filter-row" align="center" m={{ t: '1rem', b: '.75rem' }} flexWrap="nowrap">
            <Col size="4" gutterWidth="1.5rem">
              <Text textSize="subtitle" textWeight="500" textColor="navyBlue" m={{ l: '2rem' }}>
                Frameworks
              </Text>
            </Col>
            {/* <Col size="8" textAlign="right">

            </Col> */}
          </Row>
          <Row
            data-debug="table-row"
            m={{ t: '1rem', b: '.75rem' }}
            bg="white"
            rounded={{ bl: 'md', br: 'md' }}
            flexDir="row"
            align="center"
          >
            <Col size="12">
              <GridWrapper
                rawData={frameworks}
                apiStatus={apiStatus}
                columns={columns}
                data={frameworks}
                gridState={gridState}
                controlledGridState={controlledGridState}
                getData={getData}
                hasData={hasData}
                DivProps={{ m: '0rem', p: '0rem' }}
                TableProps={{ p: { y: '0rem' } }}
                setGridState={setGridState}
                widths="2fr 4fr 3fr 2fr 1fr 1fr 1fr"
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <CreateFrameworkDialog
        isOpen={createModalOpen}
        actions={{
          cancel: { on: closeCreateModal },
          close: { on: closeCreateModal },
          handlers: [{ on: onCreateFramework, after: getData }],
        }}
      />
    </Div>
  );
}

const mapStateToProps = ({ app }) => ({
  app,
});

const mapDispatchToProps = (dispatch) => ({
  enqueueNotification: (...params) => dispatch(appActions.enqueueNotification(...params)),
});

Regulatory.propTypes = {
  enqueueNotification: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(Page(Regulatory));
